import type { FormikErrors, FormikProps, FormikTouched } from 'formik'
import { type FocusEvent } from 'react'

import type { LogVolunteeringActivityFormFields } from '../LogVolunteeringActivity.types'
import { DateTimeSection } from '../components/DateTimeSection'
import { ActivityTagsSection } from '../components/ActivityTagsSection'
import { NonprofitSearchSection } from '../components/NonprofitSearchSection'

import type { Organisation } from '@percent/workplace-giving/api/search/searchOrganisations/searchOrganisations.types'

export type LogOffPlatformActivityFormProps = Readonly<{
  values: LogVolunteeringActivityFormFields
  setFieldValue: (
    field: string,
    value: any,
    shouldValidate?: boolean
  ) => Promise<void | FormikErrors<LogVolunteeringActivityFormFields>>
  setFieldTouched: (
    field: string,
    touched?: boolean | undefined,
    shouldValidate?: boolean | undefined
  ) => Promise<void | FormikErrors<LogVolunteeringActivityFormFields>>
  touched: FormikTouched<LogVolunteeringActivityFormFields>
  errors: FormikErrors<LogVolunteeringActivityFormFields>
  handleBlur: <A = HTMLInputElement>(e: FocusEvent<A>) => void
  formikInstance: FormikProps<LogVolunteeringActivityFormFields>
  allowMultipleEntries: boolean
  duplicateDates?: string[]
  showWorkingHoursCheck: boolean
  workingHoursRequired: boolean
  defaultOrg?: Organisation
  defaultCountryCode: string
  disableCountryChange: boolean
  requireNonprofitFields: boolean | null
  searchOrgsWithEligibilityCheck: boolean | null
  showOverlapWarning?: boolean
  showRewardPreview?: boolean
}>

export function LogOffPlatformActivityForm({
  values,
  touched,
  errors,
  setFieldValue,
  setFieldTouched,
  handleBlur,
  formikInstance,
  allowMultipleEntries,
  duplicateDates,
  showWorkingHoursCheck,
  workingHoursRequired,
  defaultOrg,
  defaultCountryCode,
  disableCountryChange,
  requireNonprofitFields,
  searchOrgsWithEligibilityCheck,
  showOverlapWarning,
  showRewardPreview
}: LogOffPlatformActivityFormProps) {
  return (
    <>
      <DateTimeSection
        values={values}
        touched={touched}
        errors={errors}
        setFieldValue={setFieldValue}
        setFieldTouched={setFieldTouched}
        handleBlur={handleBlur}
        formikInstance={formikInstance}
        allowMultipleEntries={allowMultipleEntries}
        duplicateDates={duplicateDates}
        showWorkingHoursCheck={showWorkingHoursCheck}
        workingHoursRequired={workingHoursRequired}
        showOverlapWarning={showOverlapWarning}
        showRewardPreview={showRewardPreview}
      />
      <ActivityTagsSection
        activitiesValue={values.activities ?? []}
        skillsValue={values.skills ?? []}
        onActivitiesChange={selectedValues => setFieldValue('activities', selectedValues)}
        onSkillsChange={selectedValues => setFieldValue('skills', selectedValues)}
        activitiesTouched={!!touched.activities}
        activitiesError={
          Array.isArray(errors.activities) ? errors.activities.join(', ') : (errors.activities as string | undefined)
        }
        onActivitiesTouch={() => setFieldTouched('activities')}
      />
      <NonprofitSearchSection
        countryValue={values.organisationCountry ?? ''}
        organisationIdValue={values.organisationId ?? ''}
        onCountryChange={countryCode => setFieldValue('organisationCountry', countryCode)}
        onOrganisationChange={orgId => {
          setFieldValue('organisationId', orgId)
          setFieldTouched('organisationId')
        }}
        countryTouched={!!touched.organisationCountry}
        countryError={errors.organisationCountry}
        organisationTouched={!!touched.organisationId}
        organisationError={errors.organisationId}
        defaultOrg={defaultOrg}
        defaultCountryCode={defaultCountryCode}
        disableCountryChange={disableCountryChange}
        requireNonprofitFields={requireNonprofitFields}
        searchOrgsWithEligibilityCheck={searchOrgsWithEligibilityCheck}
      />
    </>
  )
}
